import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { interlude } from "@/interlude";

const ALLOWED_EXTENSIONS = [".csv", ".xml", ".tsv", ".txt"];
const ALLOWED_MIME_TYPES = [
  "text/csv",
  "application/xml",
  "text/xml",
  "text/tab-separated-values",
  "text/plain",
];

const uploadFileToGCSMutationFn = async ({
  signedUrl,
  file,
}: {
  signedUrl: string;
  file: File;
}) => {
  const formData = new FormData();
  formData.append("file", file);

  const fetchOptions: RequestInit = {
    method: "POST",
    body: formData,
  };

  const response = await fetch(signedUrl, fetchOptions);

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    throw new Error(
      `Failed to upload file to GCS: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }
};

export const useFileUpload = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const generateSignedUrlMutation =
    interlude.productFeed.useGenerateSignedUrl();

  const validateFile = useHandler((file: File): boolean => {
    const fileName = file.name.toLowerCase();
    const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) =>
      fileName.endsWith(ext),
    );
    const hasValidMimeType = ALLOWED_MIME_TYPES.includes(file.type);

    if (!hasValidExtension && !hasValidMimeType) {
      enqueueSnackbar(t("productFeed.fileUpload.error.invalidFormat"), {
        variant: "error",
      });
      return false;
    }

    return true;
  });

  const handleFileSelect = useHandler((file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
    }
  });

  const handleRemoveFile = useHandler(() => {
    setSelectedFile(null);
  });

  const uploadFileToGCSMutation = useMutation({
    mutationFn: uploadFileToGCSMutationFn,
  });

  const uploadFile = useHandler(async (file: File): Promise<string> => {
    const signedUrlResponse = await generateSignedUrlMutation.mutateAsync({
      filename: file.name,
    });

    await uploadFileToGCSMutation.mutateAsync({
      signedUrl: signedUrlResponse.signedUrl,
      file,
    });

    return signedUrlResponse.gcsUri;
  });

  const isUploading =
    generateSignedUrlMutation.isPending || uploadFileToGCSMutation.isPending;

  const fileUploadUtilities = useMemo(
    () => ({
      selectedFile,
      handleFileSelect,
      handleRemoveFile,
      uploadFile,
      isUploading,
    }),
    [selectedFile, handleFileSelect, handleRemoveFile, uploadFile, isUploading],
  );

  return fileUploadUtilities;
};
